import React, { useState, useEffect, useRef, useLayoutEffect, useContext } from "react";
import { Card, Typography, Row, Col, Divider, Tag, Button, message, Empty } from "antd";
import {
  SafetyCertificateOutlined,
  UserOutlined,
  CalendarOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import { formatDateOnly } from "Common/Components/FormatDateTime";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import CertificateOfCompletion from "../../Admin/TrainingCertificate/trainingCertificate";
import { ProfileContext } from "Common/hooks/context/ProfileContext";
const { Title, Text } = Typography;
const TrainingCertificates = () => {
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [currentCertificate, setCurrentCertificate] = useState(null);
  const role = localStorage.getItem("role");
  const { isProfileComplete } = useContext(ProfileContext);
  const isBlocked = !isProfileComplete;
  let navigateTo = "";
  useEffect(() => {
    const fetchCertificates = async () => {
      const token = localStorage.getItem("token");
      const role = localStorage.getItem("role");
      const personalId = localStorage.getItem("personalId");
      let url = "";
      if (role === "Mentor") {
        url = `${process.env.REACT_APP_API_URL}/TrainingCertificate/Mentor/List?mentorId=${personalId}`;
        navigateTo = "/mentor-profile";
      } else if (role === "Learner") {
        url = `${process.env.REACT_APP_API_URL}/TrainingCertificate/Learner/List?learnerId=${personalId}`;
        navigateTo = "/learner-profile";
      } else {
        console.warn("Role not recognized:", role);
        setLoading(false);
        return;
      }
      try {
        const response = await fetch(url, {
          method: "GET",
          headers: {
            accept: "*/*",
            Authorization: `Bearer ${token}`,
          },
        });
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        setCertificates(data);
      } catch (err) {
        console.error("Error", err);
      } finally {
        setLoading(false);
      }
    };
    fetchCertificates();
  }, []);
  const getScoreColor = (score) => {
    if (score >= 80) return "green";
    if (score >= 50) return "orange";
    return "red";
  };
  const handleDownload = (cert) => {
    setCurrentCertificate(cert);
    setDownloading(true);
  };
  const certificateRef = useRef(null);
  useLayoutEffect(() => {
    const generatePDF = async () => {
      if (!currentCertificate || !certificateRef.current) return;
      try {
        // Add a slight delay to ensure rendering is complete
        await new Promise((resolve) => setTimeout(resolve, 100)); // 100ms delay
        const canvas = await html2canvas(certificateRef.current, {
          scale: 2,
          useCORS: true,
          allowTaint: true,
        });
        const imgData = canvas.toDataURL("image/png");
        const pdf = new jsPDF("landscape", "mm", "a4");
        const imgProps = pdf.getImageProperties(imgData);
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
        pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
        pdf.save(
          `${currentCertificate.firstName}_${currentCertificate.lastName
          }_${currentCertificate.trainingTitle.replace(/\s+/g, "_")}.pdf`
        );
      } catch (error) {
        console.error("Error generating PDF:", error);
        message.error("Failed to generate certificate. Please try again.");
      } finally {
        setDownloading(false);
        setCurrentCertificate(null);
      }
    };
    if (currentCertificate) {
      generatePDF();
    }
  }, [currentCertificate]);
  return (
    <>
      {currentCertificate && (
        <div style={{ position: "absolute", left: "-9999px" }}>
          <div ref={certificateRef}>
            <CertificateOfCompletion certificate={currentCertificate} />
          </div>
        </div>
      )}
      <Card loading={loading} title="Training Certificates" className="card-professional">
        <div
          style={{
            pointerEvents: isBlocked ? "none" : "auto",
            opacity: isBlocked ? 0.6 : 1,
          }}
        >
          {certificates.length === 0 ? (
            <Empty description="There are no training certificates available." />
          ) : (
            <Row gutter={[16, 16]}>
              {certificates.map((cert) => (
                <Col xs={24} sm={12} lg={8} key={cert.id}>
                  <Card
                    hoverable
                    style={{ borderRadius: "8px", height: "100%" }}
                    cover={
                      <div
                        style={{
                          background: "#1890ff",
                          padding: "16px",
                          textAlign: "center",
                          color: "white",
                        }}
                      >
                        <SafetyCertificateOutlined style={{ fontSize: "48px" }} />
                        <Title level={4} style={{ color: "white", margin: "8px 0 0" }}>
                          Certificate of Completion
                        </Title>
                      </div>
                    }
                    actions={[
                      <Button
                        key={`download-${cert.id}`}
                        type="primary"
                        icon={<DownloadOutlined />}
                        onClick={() => handleDownload(cert)}
                        disabled={isBlocked}
                        loading={downloading && currentCertificate?.id === cert.id}
                      >
                        Download Certificate
                      </Button>
                    ]}
                  >
                    <div style={{ textAlign: "center" }}>
                      <Title level={5} style={{ marginBottom: "8px" }}>
                        {cert.trainingTitle}
                      </Title>
                      <div style={{ marginBottom: "16px" }}>
                        <Tag icon={<UserOutlined />} color="blue">
                          {cert.firstName} {cert.lastName}
                        </Tag>
                      </div>
                      <Divider style={{ margin: "16px 0" }} />
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          marginBottom: "8px",
                        }}
                      >
                        <Text strong>Score:</Text>
                        <Tag color={getScoreColor(cert.finalScore)}>
                          {cert.finalScore.toFixed(2)}% ({cert.totalMarks} marks)
                        </Tag>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          marginBottom: "8px",
                        }}
                      >
                        <Text strong>Completed On:</Text>
                        <Tag icon={<CalendarOutlined />}>{formatDateOnly(cert.createdOn)}</Tag>
                      </div>
                      <div style={{ marginTop: "16px" }}>
                        <Text type="secondary">Certificate ID: {cert.id}</Text>
                      </div>
                    </div>
                  </Card>
                </Col>
              ))}
            </Row>
          )}
        </div>
      </Card>
    </>
  );
};
export default TrainingCertificates;
